import { useState, useEffect } from "react";
import { toast } from "sonner";
import FullScreenModal from "@/components/FullScreenModal";
import { ASSET_CATEGORIES, formatCurrency, convertToINR } from "@vaulzo/core/constants";
import { deriveAssetType } from "@vaulzo/core/domain/assets";
import CategorySelect from "./CategorySelect";
import CryptoHoldingsEditor from "./CryptoHoldingsEditor";
import MFHoldingsEditor from "./MFHoldingsEditor";
import StockHoldingsEditor from "./StockHoldingsEditor";
import GoldHoldingsEditor from "./GoldHoldingsEditor";
import GovtHoldingsEditor from "./GovtHoldingsEditor";
import BankHoldingsEditor from "./BankHoldingsEditor";
import LoanHoldingsEditor from "./LoanHoldingsEditor";
import BondHoldingsEditor from "./BondHoldingsEditor";
import { Loader2 } from "lucide-react";

const TITLES = {
  liquid: "Add liquid asset",
  personal: "Add personal item",
  fixed: "Add fixed asset",
};

const NAME_PLACEHOLDERS = {
  liquid: "e.g. HDFC Savings A/c",
  personal: "e.g. iPhone 16",
  fixed: "e.g. Apartment",
};

const EMPTY_HOLDING = { symbol: "BTC", units: "", buyPrice: "" };

const EMPTY_MF_HOLDING = { schemeCode: "", schemeName: "", units: "", avgNav: "", lastNav: null, navDate: "" };

const EMPTY_STOCK_HOLDING = { symbol: "", name: "", units: "", buyPrice: "", lastPrice: null };

const EMPTY_GOLD_HOLDING = { label: "", karat: "22", grams: "", buyPerGram: "" };

const EMPTY_GOVT_HOLDING = { scheme: "NPS", label: "", invested: "", current: "" };

const EMPTY_BANK_HOLDING = { label: "", currency: "INR", balance: "", buyRate: "" };

const EMPTY_LOAN_HOLDING = { label: "", amount: "" };

const EMPTY_BOND_HOLDING = { label: "", invested: "", current: "" };

/**
 * Add/edit dialog for a single asset. Owns ALL of its form state
 * (including foreign-currency entry) and hands the parent a ready
 * API payload via onSubmit(payload) — the parent only persists.
 */
export default function AssetDialog({
  isOpen,
  onClose,
  onSubmit,
  editingAsset = null,
  assetType = null,
  categories = ASSET_CATEGORIES,
  exchangeRates,
  isLoadingRates,
  cryptoPrices = [],
  isSubmitting = false,
  onUsStocks = null,
}) {
  const [category, setCategory] = useState("");

  // Foreign-currency entry (used to live in App, which made every
  // keystroke re-render the whole tree).
  const [isForeign, setIsForeign] = useState(false);
  const [foreignCurrency, setForeignCurrency] = useState("USD");
  const [foreignAmount, setForeignAmount] = useState("");

  // Crypto portfolio: one entry holds many coins, edited as rows.
  // "manual" keeps the classic type-a-value field (and is the default
  // when editing a legacy static crypto asset, so nothing changes
  // behind the user's back).
  const [cryptoManual, setCryptoManual] = useState(false);
  const [holdings, setHoldings] = useState([EMPTY_HOLDING]);

  // Mutual-fund portfolio: same idea, priced by daily NAVs.
  const [mfManual, setMfManual] = useState(false);
  const [mfHoldings, setMfHoldings] = useState([EMPTY_MF_HOLDING]);

  // US-stocks portfolio: tickers + uninvested cash + total INR converted.
  const [stocksManual, setStocksManual] = useState(false);
  const [stockHoldings, setStockHoldings] = useState([EMPTY_STOCK_HOLDING]);
  const [cashUsd, setCashUsd] = useState("");
  const [investedInr, setInvestedInr] = useState("");

  // Gold portfolio: grams per karat, priced by the IBJA domestic rates.
  const [goldManual, setGoldManual] = useState(false);
  const [goldHoldings, setGoldHoldings] = useState([EMPTY_GOLD_HOLDING]);

  // Government schemes: invested vs current, entered from statements.
  const [govtManual, setGovtManual] = useState(false);
  const [govtHoldings, setGovtHoldings] = useState([EMPTY_GOVT_HOLDING]);

  // Bank accounts: balances in native currency (FX-revalued nightly).
  const [bankManual, setBankManual] = useState(false);
  const [bankHoldings, setBankHoldings] = useState([EMPTY_BANK_HOLDING]);

  // Loans given out: money owed to the user.
  const [loansManual, setLoansManual] = useState(false);
  const [loanHoldings, setLoanHoldings] = useState([EMPTY_LOAN_HOLDING]);

  // Bonds: invested vs current per bond, entered from statements.
  const [bondsManual, setBondsManual] = useState(false);
  const [bondHoldings, setBondHoldings] = useState([EMPTY_BOND_HOLDING]);

  useEffect(() => {
    if (isOpen) {
      setCategory(editingAsset?.category || categories[0] || "");

      setIsForeign(Boolean(editingAsset?.is_foreign));
      if (editingAsset?.is_foreign && editingAsset.foreign_currency && editingAsset.foreign_amount) {
        setForeignCurrency(editingAsset.foreign_currency);
        setForeignAmount(String(editingAsset.foreign_amount));
      } else {
        setForeignCurrency("USD");
        setForeignAmount("");
      }

      const existing = editingAsset?.crypto_holdings;
      setCryptoManual(Boolean(editingAsset && editingAsset.category === "Crypto" && !existing?.length));
      setHoldings(
        existing?.length
          ? existing.map((h) => ({
              symbol: h.symbol,
              units: String(h.units),
              buyPrice: h.buy_price_usd != null ? String(h.buy_price_usd) : "",
            }))
          : [EMPTY_HOLDING]
      );

      const existingMF = editingAsset?.mf_holdings;
      setMfManual(Boolean(editingAsset && editingAsset.category === "Mutual Funds" && !existingMF?.length));
      setMfHoldings(
        existingMF?.length
          ? existingMF.map((h) => ({
              schemeCode: h.scheme_code,
              schemeName: h.scheme_name,
              units: String(h.units),
              avgNav: h.avg_nav != null ? String(h.avg_nav) : "",
              lastNav: h.last_nav || null,
              navDate: h.nav_date || "",
            }))
          : [EMPTY_MF_HOLDING]
      );

      const existingStocks = editingAsset?.stock_holdings;
      setStocksManual(
        Boolean(
          editingAsset &&
            editingAsset.category === "US Stocks" &&
            !existingStocks?.length &&
            editingAsset.cash_usd == null
        )
      );
      setStockHoldings(
        existingStocks?.length
          ? existingStocks.map((h) => ({
              symbol: h.symbol,
              name: h.name || "",
              units: String(h.units),
              buyPrice: h.buy_price_usd != null ? String(h.buy_price_usd) : "",
              lastPrice: h.last_price || null,
            }))
          : [EMPTY_STOCK_HOLDING]
      );
      setCashUsd(editingAsset?.cash_usd != null ? String(editingAsset.cash_usd) : "");
      setInvestedInr(editingAsset?.invested_inr != null ? String(editingAsset.invested_inr) : "");

      const existingGold = editingAsset?.gold_holdings;
      setGoldManual(Boolean(editingAsset && editingAsset.category === "Gold" && !existingGold?.length));
      setGoldHoldings(
        existingGold?.length
          ? existingGold.map((h) => ({
              label: h.label || "",
              karat: h.karat,
              grams: String(h.grams),
              buyPerGram: h.buy_per_gram != null ? String(h.buy_per_gram) : "",
            }))
          : [EMPTY_GOLD_HOLDING]
      );

      const existingGovt = editingAsset?.govt_holdings;
      setGovtManual(Boolean(editingAsset && editingAsset.category === "Govt Schemes" && !existingGovt?.length));
      setGovtHoldings(
        existingGovt?.length
          ? existingGovt.map((h) => ({
              scheme: h.scheme,
              label: h.label || "",
              invested: h.invested_inr != null ? String(h.invested_inr) : "",
              current: String(h.current_inr),
            }))
          : [EMPTY_GOVT_HOLDING]
      );

      const existingBank = editingAsset?.bank_holdings;
      setBankManual(Boolean(editingAsset && editingAsset.category === "Savings Account" && !existingBank?.length));
      setBankHoldings(
        existingBank?.length
          ? existingBank.map((h) => ({
              label: h.label,
              currency: h.currency,
              balance: String(h.balance),
              buyRate: h.buy_rate != null ? String(h.buy_rate) : "",
            }))
          : [EMPTY_BANK_HOLDING]
      );

      const existingLoans = editingAsset?.loan_holdings;
      setLoansManual(Boolean(editingAsset && editingAsset.category === "Loans" && !existingLoans?.length));
      setLoanHoldings(
        existingLoans?.length
          ? existingLoans.map((h) => ({ label: h.label, amount: String(h.amount_inr) }))
          : [EMPTY_LOAN_HOLDING]
      );

      const existingBonds = editingAsset?.bond_holdings;
      setBondsManual(Boolean(editingAsset && editingAsset.category === "Bonds" && !existingBonds?.length));
      setBondHoldings(
        existingBonds?.length
          ? existingBonds.map((h) => ({
              label: h.label,
              invested: h.invested_inr != null ? String(h.invested_inr) : "",
              current: String(h.current_inr),
            }))
          : [EMPTY_BOND_HOLDING]
      );
    }
  }, [isOpen, editingAsset, categories]);

  const isPersonal =
    (editingAsset?.asset_type || (assetType === "travel" ? "travel_points" : assetType) || deriveAssetType(category)) ===
    "personal";
  const heading = editingAsset ? "Edit asset" : TITLES[assetType] || "Add asset";
  const namePlaceholder = NAME_PLACEHOLDERS[assetType] || "e.g. HDFC Savings A/c";

  const cryptoMode = category === "Crypto" && !cryptoManual;
  const mfMode = category === "Mutual Funds" && !mfManual;
  const stocksMode = category === "US Stocks" && !stocksManual;
  // New US stocks are added in their own modal, from entries (onUsStocks).
  const usStocksHandoff = !editingAsset && category === "US Stocks" && Boolean(onUsStocks);
  const goldMode = category === "Gold" && !goldManual;
  const govtMode = category === "Govt Schemes" && !govtManual;
  const bankMode = category === "Savings Account" && !bankManual;
  const loansMode = category === "Loans" && !loansManual;
  const bondsMode = category === "Bonds" && !bondsManual;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (usStocksHandoff) return;
    const fd = new FormData(e.target);
    const name = fd.get("name");

    if (cryptoMode) {
      const rows = holdings
        .map((h) => ({
          symbol: (h.symbol || "").trim().toUpperCase(),
          units: parseFloat(h.units),
          buy_price_usd: parseFloat(h.buyPrice) > 0 ? parseFloat(h.buyPrice) : null,
        }))
        .filter((h) => h.symbol && h.units > 0);
      if (rows.length === 0) return;
      onSubmit({ name, category, asset_type: "crypto", crypto_holdings: rows });
      return;
    }

    if (mfMode) {
      const rows = mfHoldings
        .map((h) => ({
          scheme_code: (h.schemeCode || "").trim(),
          scheme_name: h.schemeName || "",
          units: parseFloat(h.units),
          avg_nav: parseFloat(h.avgNav) > 0 ? parseFloat(h.avgNav) : null,
        }))
        .filter((h) => h.scheme_code && h.units > 0);
      if (rows.length === 0) return;
      onSubmit({ name, category, asset_type: "mutual_fund", mf_holdings: rows });
      return;
    }

    if (stocksMode) {
      const rows = stockHoldings
        .map((h) => ({
          symbol: (h.symbol || "").trim().toUpperCase(),
          name: h.name || "",
          units: parseFloat(h.units),
          buy_price_usd: parseFloat(h.buyPrice) > 0 ? parseFloat(h.buyPrice) : null,
        }))
        .filter((h) => h.symbol && h.units > 0);
      const cash = parseFloat(cashUsd) > 0 ? parseFloat(cashUsd) : 0;
      if (rows.length === 0 && cash <= 0) {
        toast.error("Add at least one holding or a cash amount");
        return;
      }
      onSubmit({
        name,
        category,
        asset_type: "us_stocks",
        stock_holdings: rows,
        cash_usd: cash,
        invested_inr: parseFloat(investedInr) > 0 ? parseFloat(investedInr) : null,
      });
      return;
    }

    if (goldMode) {
      const rows = goldHoldings
        .map((h) => ({
          label: (h.label || "").trim(),
          karat: h.karat,
          grams: parseFloat(h.grams),
          buy_per_gram: parseFloat(h.buyPerGram) > 0 ? parseFloat(h.buyPerGram) : null,
        }))
        .filter((h) => h.grams > 0);
      if (rows.length === 0) return;
      onSubmit({ name, category, asset_type: "gold", gold_holdings: rows });
      return;
    }

    if (govtMode) {
      const rows = govtHoldings
        .map((h) => ({
          scheme: h.scheme,
          label: h.scheme === "OTHER" ? (h.label || "").trim() : "",
          invested_inr: parseFloat(h.invested) > 0 ? parseFloat(h.invested) : null,
          current_inr: parseFloat(h.current),
        }))
        .filter((h) => h.current_inr > 0);
      if (rows.length === 0) return;
      onSubmit({ name, category, asset_type: "govt_schemes", govt_holdings: rows });
      return;
    }

    if (bankMode) {
      const rows = bankHoldings
        .map((h) => ({
          label: (h.label || "").trim(),
          currency: h.currency,
          balance: parseFloat(h.balance),
          buy_rate: h.currency !== "INR" && parseFloat(h.buyRate) > 0 ? parseFloat(h.buyRate) : null,
        }))
        .filter((h) => h.label && h.balance >= 0 && !isNaN(h.balance));
      if (rows.length === 0) return;
      onSubmit({ name, category, asset_type: "bank_accounts", bank_holdings: rows });
      return;
    }

    if (loansMode) {
      const rows = loanHoldings
        .map((h) => ({ label: (h.label || "").trim(), amount_inr: parseFloat(h.amount) }))
        .filter((h) => h.label && h.amount_inr > 0);
      if (rows.length === 0) return;
      onSubmit({ name, category, asset_type: "loans", loan_holdings: rows });
      return;
    }

    if (bondsMode) {
      const rows = bondHoldings
        .map((h) => ({
          label: (h.label || "").trim(),
          invested_inr: parseFloat(h.invested) > 0 ? parseFloat(h.invested) : null,
          current_inr: parseFloat(h.current),
        }))
        .filter((h) => h.label && h.current_inr > 0);
      if (rows.length === 0) return;
      onSubmit({ name, category, asset_type: "bonds", bond_holdings: rows });
      return;
    }

    // Classic path: a value typed in ₹ or a foreign currency.
    let currentValue;
    let foreign = false;
    if (isForeign && foreignAmount) {
      const amt = parseFloat(foreignAmount);
      if (isNaN(amt)) {
        toast.error("Please enter a valid foreign currency amount");
        return;
      }
      currentValue = convertToINR(amt, foreignCurrency, exchangeRates);
      foreign = true;
    } else {
      currentValue = parseFloat(fd.get("current_value"));
      if (isNaN(currentValue)) {
        toast.error("Please enter a valid amount");
        return;
      }
    }

    const normalizedType = assetType === "travel" ? "travel_points" : assetType;
    let purchasePrice = null;
    if (isPersonal) {
      purchasePrice = parseFloat(fd.get("purchase_price"));
      if (!(purchasePrice > 0)) purchasePrice = currentValue;
    }
    onSubmit({
      name,
      category,
      current_value: currentValue,
      is_foreign: foreign,
      foreign_currency: foreign ? foreignCurrency : null,
      foreign_amount: foreign ? parseFloat(foreignAmount) : null,
      ...(isPersonal && { purchase_price: purchasePrice }),
      // Existing assets keep their type; new ones take the tab's type,
      // falling back to deriving it from the category.
      asset_type: editingAsset?.asset_type || normalizedType || deriveAssetType(category),
    });
  };

  return (
    <FullScreenModal open={isOpen} onClose={onClose}>
      <form onSubmit={handleSubmit} className="w-full max-w-[520px]">
        <div className="mb-[14px] text-[12px] uppercase tracking-[0.14em] text-vaulzo-fainter">Asset</div>
        <h2 className="m-0 mb-11 text-[34px] font-bold tracking-[-0.03em] text-vaulzo-ink">{heading}</h2>

        <div className="flex flex-col gap-[30px]">
          {/* Name */}
          <label className={usStocksHandoff ? "hidden" : "block"}>
            <span className="mb-[10px] block text-[13px] font-semibold text-vaulzo-muted">Name</span>
            <input
              name="name"
              required={!usStocksHandoff}
              placeholder={
                cryptoMode
                  ? "e.g. Crypto portfolio"
                  : mfMode
                    ? "e.g. Mutual funds"
                    : stocksMode
                      ? "e.g. US stocks"
                      : goldMode
                        ? "e.g. Gold jewellery"
                        : govtMode
                          ? "e.g. Govt schemes"
                          : bankMode
                            ? "e.g. Savings accounts"
                            : loansMode
                              ? "e.g. Loans given"
                              : bondsMode
                                ? "e.g. Bonds"
                                : namePlaceholder
              }
              defaultValue={editingAsset?.name}
              className="w-full border-0 border-b-[1.5px] border-vaulzo-input-line bg-transparent py-2 text-base text-vaulzo-ink outline-none transition-colors placeholder:text-vaulzo-subtle focus:border-vaulzo-accent"
            />
          </label>

          {/* Category (searchable) */}
          <div className="block">
            <span className="mb-[10px] block text-[13px] font-semibold text-vaulzo-muted">Category</span>
            <CategorySelect value={category} onChange={setCategory} categories={categories} />
          </div>

          {usStocksHandoff ? (
            <div className="rounded-xl bg-vaulzo-row/60 px-5 py-4">
              <div className="text-[15px] font-semibold text-vaulzo-ink">US stocks have their own view</div>
              <p className="mb-4 mt-1 text-[13px] leading-relaxed text-vaulzo-muted">
                Record each ₹ → $ conversion, buy, sell and transfer there; holdings, cash per broker and money put in
                are worked out from those entries.
              </p>
              <button
                type="button"
                onClick={onUsStocks}
                disabled={isSubmitting}
                className="flex items-center rounded-[11px] bg-vaulzo-accent px-[22px] py-[11px] text-[14px] font-semibold text-vaulzo-on-fill transition-colors hover:bg-vaulzo-accent-hover disabled:opacity-60"
              >
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : "Open US stocks"}
              </button>
            </div>
          ) : cryptoMode ? (
            <>
              <CryptoHoldingsEditor
                holdings={holdings}
                setHoldings={setHoldings}
                cryptoPrices={cryptoPrices}
                exchangeRates={exchangeRates}
              />
              <button
                type="button"
                onClick={() => setCryptoManual(true)}
                className="self-start text-[12px] font-medium text-vaulzo-fainter transition-colors hover:text-vaulzo-accent"
              >
                Enter a value manually instead
              </button>
            </>
          ) : mfMode ? (
            <>
              <MFHoldingsEditor holdings={mfHoldings} setHoldings={setMfHoldings} />
              <button
                type="button"
                onClick={() => setMfManual(true)}
                className="self-start text-[12px] font-medium text-vaulzo-fainter transition-colors hover:text-vaulzo-accent"
              >
                Enter a value manually instead
              </button>
            </>
          ) : bankMode ? (
            <>
              <BankHoldingsEditor
                holdings={bankHoldings}
                setHoldings={setBankHoldings}
                exchangeRates={exchangeRates}
              />
              <button
                type="button"
                onClick={() => setBankManual(true)}
                className="self-start text-[12px] font-medium text-vaulzo-fainter transition-colors hover:text-vaulzo-accent"
              >
                Enter a value manually instead
              </button>
            </>
          ) : bondsMode ? (
            <>
              <BondHoldingsEditor holdings={bondHoldings} setHoldings={setBondHoldings} />
              <button
                type="button"
                onClick={() => setBondsManual(true)}
                className="self-start text-[12px] font-medium text-vaulzo-fainter transition-colors hover:text-vaulzo-accent"
              >
                Enter a value manually instead
              </button>
            </>
          ) : loansMode ? (
            <>
              <LoanHoldingsEditor holdings={loanHoldings} setHoldings={setLoanHoldings} />
              <button
                type="button"
                onClick={() => setLoansManual(true)}
                className="self-start text-[12px] font-medium text-vaulzo-fainter transition-colors hover:text-vaulzo-accent"
              >
                Enter a value manually instead
              </button>
            </>
          ) : govtMode ? (
            <>
              <GovtHoldingsEditor holdings={govtHoldings} setHoldings={setGovtHoldings} />
              <button
                type="button"
                onClick={() => setGovtManual(true)}
                className="self-start text-[12px] font-medium text-vaulzo-fainter transition-colors hover:text-vaulzo-accent"
              >
                Enter a value manually instead
              </button>
            </>
          ) : goldMode ? (
            <>
              <GoldHoldingsEditor holdings={goldHoldings} setHoldings={setGoldHoldings} />
              <button
                type="button"
                onClick={() => setGoldManual(true)}
                className="self-start text-[12px] font-medium text-vaulzo-fainter transition-colors hover:text-vaulzo-accent"
              >
                Enter a value manually instead
              </button>
            </>
          ) : stocksMode ? (
            <>
              <StockHoldingsEditor
                holdings={stockHoldings}
                setHoldings={setStockHoldings}
                cashUsd={cashUsd}
                setCashUsd={setCashUsd}
                investedInr={investedInr}
                setInvestedInr={setInvestedInr}
                exchangeRates={exchangeRates}
              />
              <button
                type="button"
                onClick={() => setStocksManual(true)}
                className="self-start text-[12px] font-medium text-vaulzo-fainter transition-colors hover:text-vaulzo-accent"
              >
                Enter a value manually instead
              </button>
            </>
          ) : (
            /* Value */
            <div className="block">
              <div className="mb-[10px] flex items-center justify-between">
                <span className="text-[13px] font-semibold text-vaulzo-muted">Current value</span>
                <span className="flex gap-4">
                  {category === "Crypto" && (
                    <button
                      type="button"
                      onClick={() => setCryptoManual(false)}
                      className="text-[12px] font-medium text-vaulzo-accent transition-colors hover:text-vaulzo-accent-hover"
                    >
                      Track coins & units
                    </button>
                  )}
                  {category === "Mutual Funds" && (
                    <button
                      type="button"
                      onClick={() => setMfManual(false)}
                      className="text-[12px] font-medium text-vaulzo-accent transition-colors hover:text-vaulzo-accent-hover"
                    >
                      Track funds & units
                    </button>
                  )}
                  {category === "US Stocks" && (
                    <button
                      type="button"
                      onClick={() => setStocksManual(false)}
                      className="text-[12px] font-medium text-vaulzo-accent transition-colors hover:text-vaulzo-accent-hover"
                    >
                      Track holdings
                    </button>
                  )}
                  {category === "Gold" && (
                    <button
                      type="button"
                      onClick={() => setGoldManual(false)}
                      className="text-[12px] font-medium text-vaulzo-accent transition-colors hover:text-vaulzo-accent-hover"
                    >
                      Track grams & karat
                    </button>
                  )}
                  {category === "Govt Schemes" && (
                    <button
                      type="button"
                      onClick={() => setGovtManual(false)}
                      className="text-[12px] font-medium text-vaulzo-accent transition-colors hover:text-vaulzo-accent-hover"
                    >
                      Track schemes
                    </button>
                  )}
                  {category === "Savings Account" && (
                    <button
                      type="button"
                      onClick={() => setBankManual(false)}
                      className="text-[12px] font-medium text-vaulzo-accent transition-colors hover:text-vaulzo-accent-hover"
                    >
                      Track accounts
                    </button>
                  )}
                  {category === "Loans" && (
                    <button
                      type="button"
                      onClick={() => setLoansManual(false)}
                      className="text-[12px] font-medium text-vaulzo-accent transition-colors hover:text-vaulzo-accent-hover"
                    >
                      Track loans
                    </button>
                  )}
                  {category === "Bonds" && (
                    <button
                      type="button"
                      onClick={() => setBondsManual(false)}
                      className="text-[12px] font-medium text-vaulzo-accent transition-colors hover:text-vaulzo-accent-hover"
                    >
                      Track bonds
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setIsForeign(!isForeign)}
                    className={`text-[12px] font-medium transition-colors ${
                      isForeign ? "text-vaulzo-accent" : "text-vaulzo-fainter hover:text-vaulzo-accent"
                    }`}
                  >
                    {isForeign ? "Enter in ₹" : "Foreign currency"}
                  </button>
                </span>
              </div>

              {isForeign ? (
                <>
                  <div className="flex items-baseline gap-[10px] border-b-[1.5px] border-vaulzo-input-line focus-within:border-vaulzo-accent">
                    <select
                      value={foreignCurrency}
                      onChange={(e) => setForeignCurrency(e.target.value)}
                      className="bg-transparent py-2 text-[18px] font-semibold text-vaulzo-ink outline-none"
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                    <input
                      type="number"
                      step="0.01"
                      inputMode="decimal"
                      value={foreignAmount}
                      onChange={(e) => setForeignAmount(e.target.value)}
                      placeholder="0"
                      className="num flex-1 border-0 bg-transparent py-2 text-[20px] font-semibold text-vaulzo-ink outline-none placeholder:text-vaulzo-subtle"
                    />
                  </div>
                  {foreignAmount && !isNaN(parseFloat(foreignAmount)) && (
                    <div className="mt-2 text-[12px] text-vaulzo-fainter">
                      {isLoadingRates
                        ? "Loading rates…"
                        : `≈ ${formatCurrency(convertToINR(parseFloat(foreignAmount), foreignCurrency, exchangeRates))}`}
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-baseline gap-[10px] border-b-[1.5px] border-vaulzo-input-line focus-within:border-vaulzo-accent">
                  <span className="text-[20px] text-vaulzo-fainter">₹</span>
                  <input
                    name="current_value"
                    type="number"
                    step="0.01"
                    required
                    inputMode="numeric"
                    defaultValue={editingAsset?.current_value}
                    placeholder="0"
                    className="num flex-1 border-0 bg-transparent py-2 text-[20px] font-semibold text-vaulzo-ink outline-none placeholder:text-vaulzo-subtle"
                  />
                </div>
              )}
              {isPersonal && (
                <div className="mt-7">
                  <div className="mb-[10px] flex items-baseline justify-between">
                    <span className="text-[13px] font-semibold text-vaulzo-muted">Bought for</span>
                    <span className="text-[12px] text-vaulzo-fainter">Leave blank to use the value above</span>
                  </div>
                  <div className="flex items-baseline gap-[10px] border-b-[1.5px] border-vaulzo-input-line focus-within:border-vaulzo-accent">
                    <span className="text-[20px] text-vaulzo-fainter">₹</span>
                    <input
                      name="purchase_price"
                      type="number"
                      step="0.01"
                      inputMode="numeric"
                      defaultValue={editingAsset?.purchase_price ?? editingAsset?.current_value}
                      placeholder="0"
                      className="num flex-1 border-0 bg-transparent py-2 text-[20px] font-semibold text-vaulzo-ink outline-none placeholder:text-vaulzo-subtle"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-[52px] flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-[11px] bg-vaulzo-row px-[26px] py-[14px] text-[15px] font-semibold text-vaulzo-muted transition-colors hover:bg-vaulzo-soft disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`${usStocksHandoff ? "hidden" : "flex"} items-center rounded-[11px] bg-vaulzo-accent px-[30px] py-[14px] text-[15px] font-semibold text-vaulzo-on-fill transition-colors hover:bg-vaulzo-accent-hover disabled:opacity-60`}
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : editingAsset ? (
              "Save changes"
            ) : (
              "Add asset"
            )}
          </button>
        </div>
      </form>
    </FullScreenModal>
  );
}
